import type { SupplierDocument } from '@tandemone/types'
import { EVENTS } from '@tandemone/types'
import { useQueries } from '@tanstack/react-query'
import { ChevronDown, Download } from 'lucide-react'
import { type ReactNode, useMemo, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from '@/components/ui/carousel'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { track } from '@/domains/observability'
import { formatDate } from '@/lib/format-date'

import {
  type DocumentDownloadResult,
  type PreviewDocument,
  DocumentPreviewDialog
} from './document-preview-dialog'
import { getFileIcon } from './file-icon'
import { PdfThumbnail } from './pdf-thumbnail'
import { ProfileEmpty } from './profile-empty'
import { VideoThumbnail } from './video-thumbnail'

/** identifiers the gallery needs to fire supplier_document_viewed when a
 * document is opened. omitted by callers that view a supplier's own docs
 * (the company-self Documents page) so a supplier doesn't get counted as
 * a viewer of their own files.
 */
export type DocumentsGalleryTracking = {
  supplierCompanyId: string
  supplierId: string
}

type MediaCategory = PreviewDocument['category']

type MediaItem = {
  documentId: string
  fileName: string
  contentType: string
  createdAt: string
  category: MediaCategory
}

const CATEGORY_LABELS: Record<MediaCategory, string> = {
  facility: 'Facility',
  equipment: 'Equipment',
  product: 'Product',
  management_team: 'Management team',
  other: 'Other',
  logo: 'Logo',
  certificate: 'Certificate'
}

/** one color per category. certs all collapse to a single color since the
 * category is the taxonomy here, not the individual cert family.
 */
const CATEGORY_BADGE_CLASS: Record<MediaCategory, string> = {
  certificate:
    'bg-blue-100 text-blue-900 border-transparent dark:bg-blue-950 dark:text-blue-200',
  facility:
    'bg-emerald-100 text-emerald-900 border-transparent dark:bg-emerald-950 dark:text-emerald-200',
  equipment:
    'bg-amber-100 text-amber-900 border-transparent dark:bg-amber-950 dark:text-amber-200',
  product:
    'bg-violet-100 text-violet-900 border-transparent dark:bg-violet-950 dark:text-violet-200',
  management_team:
    'bg-rose-100 text-rose-900 border-transparent dark:bg-rose-950 dark:text-rose-200',
  other:
    'bg-slate-100 text-slate-900 border-transparent dark:bg-slate-800 dark:text-slate-200',
  logo: 'bg-cyan-100 text-cyan-900 border-transparent dark:bg-cyan-950 dark:text-cyan-200'
}

/** carousel of thumbnails + a sortable-looking table, both opening the
 * scroll-through preview dialog. renders `emptyState` (or a default message)
 * when there's nothing to show, so callers own the empty framing.
 */
export function DocumentsGallery({
  documents,
  fetchUrl,
  emptyState,
  tracking,
  collapsible = true,
  defaultOpen = false
}: {
  documents: SupplierDocument[]
  fetchUrl: (documentId: string) => Promise<DocumentDownloadResult>
  emptyState?: ReactNode
  tracking?: DocumentsGalleryTracking
  /** the detailed row list (mobile cards + desktop table) hides behind a
   *  toggle by default. callers whose whole job is reviewing every row (the
   *  super due-diligence packet, the cert-review queue) pass false to keep
   *  it always expanded, matching today's pre-ENG-61 behavior. */
  collapsible?: boolean
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  const items: MediaItem[] = useMemo(
    () =>
      documents
        .map(doc => ({
          documentId: doc.id,
          fileName: doc.fileName,
          contentType: doc.contentType,
          createdAt: doc.createdAt,
          category: doc.category as MediaCategory
        }))
        .sort(
          (left, right) =>
            new Date(right.createdAt).getTime() -
            new Date(left.createdAt).getTime()
        ),
    [documents]
  )

  const previewDocuments: PreviewDocument[] = useMemo(
    () =>
      items.map(item => ({
        id: item.documentId,
        fileName: item.fileName,
        contentType: item.contentType,
        category: item.category,
        createdAt: item.createdAt
      })),
    [items]
  )

  const [previewIndex, setPreviewIndex] = useState<number | null>(null)

  const previews = useQueries({
    queries: items.map(item => ({
      queryKey: ['document-preview', item.documentId],
      queryFn: () => fetchUrl(item.documentId),
      staleTime: 60_000
    }))
  })

  if (items.length === 0) {
    return (
      <>{emptyState ?? <ProfileEmpty message='No documents uploaded.' />}</>
    )
  }

  function openPreview(item: MediaItem): void {
    const index = items.indexOf(item)
    if (index < 0) return
    setPreviewIndex(index)
    /** event only fires when a caller supplies tracking, the supplier
     * viewing their own documents page passes none, so self-views don't
     * inflate the insights count. the 'certificate' branch is a typescript
     * possibility from the wider MediaCategory union but never reachable
     * here (DocumentsGallery is fed SupplierDocument[], whose category
     * doesn't include 'certificate'), cert previews land in
     * supplier_certificate_viewed via CertificatesGallery instead.
     */
    if (tracking && item.category !== 'certificate') {
      track(EVENTS.SUPPLIER_DOCUMENT_VIEWED, {
        viewed_company_id: tracking.supplierCompanyId,
        viewed_supplier_id: tracking.supplierId,
        document_id: item.documentId,
        category: item.category,
        file_name: item.fileName
      })
    }
  }

  return (
    <>
      <div className='flex flex-col gap-5'>
        <Carousel opts={{ align: 'start', dragFree: true }} className='w-full'>
          <CarouselContent className='-ml-3'>
            {items.map((item, idx) => {
              const preview = previews[idx]
              const isImage = item.contentType.startsWith('image/')
              const isPdf = item.contentType === 'application/pdf'
              const isVideo = item.contentType.startsWith('video/')
              const { Icon, className: iconClassName } = getFileIcon(
                item.contentType
              )
              const iconFallback = (
                <div className='flex size-full flex-col items-center justify-center gap-2 p-2'>
                  <Icon className={`size-8 ${iconClassName}`} />
                  <span className='line-clamp-2 text-center text-[10px] text-muted-foreground'>
                    {item.fileName}
                  </span>
                </div>
              )
              return (
                <CarouselItem
                  key={item.documentId}
                  className='basis-1/2 pl-3 sm:basis-1/3 md:basis-1/4 lg:basis-1/5'
                >
                  <button
                    type='button'
                    onClick={() => openPreview(item)}
                    className='group relative block aspect-square w-full cursor-pointer overflow-hidden rounded-md border border-border bg-muted'
                    aria-label={`Open ${item.fileName}`}
                  >
                    {isImage && preview?.data ? (
                      <img
                        src={preview.data.previewUrl}
                        alt={item.fileName}
                        loading='lazy'
                        className='size-full object-cover transition-transform group-hover:scale-105'
                      />
                    ) : isPdf && preview?.data ? (
                      <PdfThumbnail
                        documentId={item.documentId}
                        url={preview.data.previewUrl}
                        fileName={item.fileName}
                        className='transition-transform group-hover:scale-105'
                        fallback={iconFallback}
                      />
                    ) : isVideo && preview?.data ? (
                      <VideoThumbnail
                        url={preview.data.previewUrl}
                        fileName={item.fileName}
                        className='transition-transform group-hover:scale-105'
                      />
                    ) : (
                      iconFallback
                    )}
                    <Badge
                      className={`absolute top-1.5 left-1.5 max-w-[calc(100%-2.5rem)] truncate ${CATEGORY_BADGE_CLASS[item.category]}`}
                    >
                      {CATEGORY_LABELS[item.category]}
                    </Badge>
                  </button>
                </CarouselItem>
              )
            })}
          </CarouselContent>
          {/* embla drags on touch, so on a phone the arrows would only sit
              over the end thumbnails. */}
          <CarouselPrevious className='left-2 hidden size-9 border-transparent bg-accent text-white shadow-sm hover:bg-accent/90 disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100 sm:inline-flex' />
          <CarouselNext className='right-2 hidden size-9 border-transparent bg-accent text-white shadow-sm hover:bg-accent/90 disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100 sm:inline-flex' />
        </Carousel>

        <Collapsible open={!collapsible || open} onOpenChange={setOpen}>
          {collapsible ? (
            <CollapsibleTrigger className='flex cursor-pointer items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground'>
              <ChevronDown
                className={`size-4 transition-transform ${open ? '' : '-rotate-90'}`}
              />
              {open ? 'Hide' : 'Show'} documents
              <span className='text-xs font-normal'>({items.length})</span>
            </CollapsibleTrigger>
          ) : null}
          <CollapsibleContent className='flex flex-col gap-5 pt-3'>
            <div className='flex flex-col gap-3 md:hidden'>
              {items.map((item, idx) => {
                const { Icon, className: iconClassName } = getFileIcon(
                  item.contentType
                )
                const preview = previews[idx]
                return (
                  <div
                    key={item.documentId}
                    className='flex flex-col gap-3 rounded-lg border border-border bg-page p-4'
                  >
                    <button
                      type='button'
                      onClick={() => openPreview(item)}
                      className='flex min-w-0 cursor-pointer items-start gap-2 text-left'
                    >
                      <Icon
                        className={`mt-0.5 size-4 shrink-0 ${iconClassName}`}
                      />
                      <span className='line-clamp-2 min-w-0 text-sm font-medium break-words text-foreground'>
                        {item.fileName}
                      </span>
                    </button>
                    <div className='flex flex-wrap items-center justify-between gap-x-3 gap-y-2'>
                      <div className='flex flex-wrap items-center gap-2 text-xs text-muted-foreground'>
                        <Badge className={CATEGORY_BADGE_CLASS[item.category]}>
                          {CATEGORY_LABELS[item.category]}
                        </Badge>
                        <span>{formatDate(item.createdAt)}</span>
                      </div>
                      {preview?.data ? (
                        <a
                          href={preview.data.downloadUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                          download
                          aria-label={`Download ${item.fileName}`}
                          className='inline-grid size-11 cursor-pointer place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground md:size-8'
                        >
                          <Download className='size-4' />
                        </a>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>

            <Table className='hidden md:table'>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead className='w-12' />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item, idx) => {
                  const { Icon, className: iconClassName } = getFileIcon(
                    item.contentType
                  )
                  const preview = previews[idx]
                  return (
                    <TableRow
                      key={item.documentId}
                      onClick={() => openPreview(item)}
                      className='cursor-pointer'
                    >
                      <TableCell className='max-w-[16rem]'>
                        <div className='inline-flex max-w-full items-center gap-2'>
                          <Icon
                            className={`size-4 shrink-0 ${iconClassName}`}
                          />
                          <span className='truncate text-sm font-medium text-foreground'>
                            {item.fileName}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={CATEGORY_BADGE_CLASS[item.category]}>
                          {CATEGORY_LABELS[item.category]}
                        </Badge>
                      </TableCell>
                      <TableCell className='text-xs text-muted-foreground'>
                        {formatDate(item.createdAt)}
                      </TableCell>
                      <TableCell>
                        {preview?.data ? (
                          <a
                            href={preview.data.downloadUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            download
                            aria-label={`Download ${item.fileName}`}
                            onClick={event => event.stopPropagation()}
                            className='inline-grid size-8 cursor-pointer place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
                          >
                            <Download className='size-4' />
                          </a>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </CollapsibleContent>
        </Collapsible>
      </div>
      <DocumentPreviewDialog
        documents={previewDocuments}
        initialIndex={previewIndex}
        onClose={() => setPreviewIndex(null)}
        fetchUrl={fetchUrl}
      />
    </>
  )
}
